import { Dictionary } from '@ngrx/entity';
import { createSelector } from '@ngrx/store';
import {
  IRecentPurchaseState,
  ListSearchSortFilterOptions,
  RecentPurchaseState,
  recentPurchaseAdapter,
  selectRecentPurchaseSearchSortFilterOptions,
  selectRecentPurchaseState,
} from '@usf/ngrx-list';
import moment from 'moment';
import { PlatformEnum } from '../../../../shared/constants/platform-type.enum';
import { Product } from '../../../../shared/models/product.model';
import { productWithAlternativesSelector } from '../../../../shared/selectors/product.selectors';
import {
  AddToInventoryViewModel,
  SelectableRecentPurchaseProduct,
} from '../../../models/add-to-inventory-view-model.model';
import { calculateNoQtyBoxWithLineNumberProductHeight } from '../../../../product-detail/utils/product-card.util';

export const selectSelectedRecentPurchaseIds = () =>
  createSelector(
    selectRecentPurchaseState,
    (recentPurchaseState: IRecentPurchaseState): number[] => {
      const recentPurchaseIdsToReturn: number[] = [];
      for (const key of recentPurchaseState.ids) {
        const keyAsNumber = Number(key);
        if (recentPurchaseState.selections[keyAsNumber]) {
          recentPurchaseIdsToReturn.push(keyAsNumber);
        }
      }
      return recentPurchaseIdsToReturn;
    },
  );

export const selectRecentPurchases = () =>
  createSelector(
    recentPurchaseAdapter.getSelectors(selectRecentPurchaseState)
      .selectEntities,
    (
      recentPurchaseDictionary: Dictionary<RecentPurchaseState>,
    ): RecentPurchaseState[] => {
      const recentPurchasesToReturn: RecentPurchaseState[] = [];
      for (const key in recentPurchaseDictionary) {
        recentPurchasesToReturn.push(recentPurchaseDictionary[key]);
      }
      return recentPurchasesToReturn;
    },
  );

export const selectAddToInventoryViewModel = (
  pastPurchaseDays: number,
  platformType: PlatformEnum,
) =>
  createSelector(
    selectRecentPurchaseState,
    productWithAlternativesSelector,
    selectRecentPurchaseSearchSortFilterOptions,
    (
      recentPurchaseState: IRecentPurchaseState,
      products: Map<number, Product>,
      recentPurchaseSearchSortFilterOptions: ListSearchSortFilterOptions,
    ): AddToInventoryViewModel => {
      let selectableRecentPurchaseProducts: SelectableRecentPurchaseProduct[] = [];
      let numberOfProductsSelected = 0;
      let itemHeights: number[] = calculateHeaderHeight(platformType);
      let indexOffset = 1;
      const dateInPastPurchaseDays = getDateFromNumberOfDaysAgo(
        pastPurchaseDays,
      );

      for (const key in recentPurchaseState.entities) {
        const recentPurchase: RecentPurchaseState =
          recentPurchaseState.entities[key];
        const recentPurchaseDate = getDateFromTimeStamp(
          recentPurchase?.purchaseDtm1,
        );
        if (moment(dateInPastPurchaseDays).isBefore(recentPurchaseDate)) {
          const product = products.get(recentPurchase.productNumber);
          if (
            product.productNumber
              .toString()
              .includes(
                recentPurchaseSearchSortFilterOptions.searchKey.toLowerCase(),
              ) ||
            product.summary?.brand
              ?.toLowerCase()
              .includes(
                recentPurchaseSearchSortFilterOptions.searchKey.toLowerCase(),
              ) ||
            product.summary?.productDescTxtl
              ?.toLowerCase()
              .includes(
                recentPurchaseSearchSortFilterOptions.searchKey.toLowerCase(),
              )
          ) {
            const selectableRecentPurchaseProduct = createSelectableRecentPurchaseProduct(
              product,
              recentPurchaseState.selections[product.productNumber],
            );

            const itemHeight = calculateNoQtyBoxWithLineNumberProductHeight(
              platformType,
              selectableRecentPurchaseProduct,
            );
            selectableRecentPurchaseProducts.push(
              selectableRecentPurchaseProduct,
            );
            itemHeights.push(itemHeight);
            if (selectableRecentPurchaseProduct?.isSelected) {
              numberOfProductsSelected++;
            }
          }
        }
      }

      return {
        products: selectableRecentPurchaseProducts,
        numberOfProductsSelected,
        itemHeights,
        indexOffset,
        pastPurchaseDays,
        searchKey: recentPurchaseSearchSortFilterOptions.searchKey,
      } as AddToInventoryViewModel;
    },
  );

const getDateFromTimeStamp = (timeStamp: string | Date) =>
  moment.utc(timeStamp).utcOffset(0).format('MM/DD/YYYY');

const getDateFromNumberOfDaysAgo = (pastPurchaseDays: number) =>
  moment
    .utc()
    .utcOffset(0)
    .subtract(pastPurchaseDays, 'days')
    .format('MM/DD/YYYY');

const createSelectableRecentPurchaseProduct = (
  product: Product,
  isSelected: boolean,
) => ({
  ...product,
  hasAlternative: false,
  hideQuantityBoxes: true,
  isSelected,
});

const calculateHeaderHeight = (platformType: PlatformEnum): number[] => {
  let itemHeights: number[] = [];
  const desktopTabletSubHeaderHeight = 65.59;
  const mobileSubHeaderHeight = 99.19;
  if (platformType === PlatformEnum.mobile) {
    itemHeights.push(mobileSubHeaderHeight);
  } else {
    itemHeights.push(desktopTabletSubHeaderHeight);
  }
  return itemHeights;
};
